import { ThroughputRecordStates } from '../../types/autocoderThroughput'

/**
 * The lifetime record-state panel's content, as data.
 *
 * Kept out of the component for the same reason `laneQueueModel` and
 * `llmLanesModel` are: the decisions here — when to show nothing at all, and
 * when a number needs a caveat — are worth testing without mounting a page full
 * of charts.
 */
export interface RecordStateTile {
  label: string
  value: string
  hint?: string
  /** The total leads the row and is styled apart from its own parts. */
  emphasis?: boolean
}

/** Cleared only by an operator: there has been no automatic retry sweep of any
 *  kind since T12/T19, so the panel must not imply one is coming. */
const FAILED_HINT = 'Cleared only by retry'

/**
 * Returns null when there is no honest panel to draw — the customer-wide view,
 * where summing records by state spans unrelated pipelines, or a payload from
 * before the field existed.
 *
 * A scoped pipeline holding zero records still returns tiles: "nothing has been
 * sent here yet" is a fact worth showing, and a vanishing panel reads as a bug.
 */
export const recordStateTiles = (
  states: ThroughputRecordStates | undefined,
): RecordStateTile[] | null => {
  if (!states?.scoped) return null

  const n = (v: number) => (v ?? 0).toLocaleString('en-GB')

  return [
    { label: 'Total records', value: n(states.total), emphasis: true },
    { label: 'Pending', value: n(states.pending) },
    { label: 'Queued', value: n(states.queued) },
    { label: 'Running', value: n(states.running) },
    { label: 'Completed', value: n(states.completed) },
    {
      label: 'Failed',
      value: n(states.failed),
      hint: states.failed > 0 ? FAILED_HINT : undefined,
    },
  ]
}
